<template>
  <div>
    <!-- Dialog de Histórico (lista de notas) -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-book-open-variant</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAddForm"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Nova nota</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="notes.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 140px">
                    Data
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 130px">
                    Tipo
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Conteúdo
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in notes" :key="item.id" class="history-row">
                  <td class="text-no-wrap pa-4">
                    <span class="body-2">{{ formatDate(item.created_at) }}</span>
                  </td>
                  <td class="pa-4">
                    <v-chip
                      x-small
                      label
                      :color="kindColor(item.kind)"
                      text-color="white"
                    >
                      {{ kindLabel(item.kind) }}
                    </v-chip>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ item.content }}</span>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="catViolet"
                          class="action-btn mr-1"
                          @click="openEditForm(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-book-open-outline</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhuma nota registrada.</p>
            <v-btn color="catViolet" outlined small class="mt-4" @click="openAddForm">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeira nota
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar/Editar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">
              {{ editingId ? "mdi-pencil" : "mdi-plus-circle-outline" }}
            </v-icon>
            <span class="ds-dialog-title">
              {{ editingId ? "Editar nota" : "Nova nota" }}
            </span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Obrigatórios vazios ficam em vermelho ao tentar salvar (validate). -->
          <v-form ref="entryForm" @submit.prevent>
            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-note-text</v-icon>
                  <span>Nota</span>
                </div>
              </div>
              <v-select
                v-model="form.kind"
                :items="kindOptions"
                label="Tipo de nota *"
                :rules="[required]"
                outlined
                dense
                color="catViolet"
                item-color="catViolet"
                prepend-inner-icon="mdi-tag-outline"
                hide-details="auto"
                class="mb-3"
              ></v-select>
              <v-textarea
                v-model="form.content"
                label="Conteúdo *"
                outlined
                dense
                rows="5"
                auto-grow
                color="catViolet"
                :rules="[(v) => !!(v && v.trim()) || 'Escreva o conteúdo da nota']"
                hide-details="auto"
                autofocus
              ></v-textarea>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeForm">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="catViolet"
            dark
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { formatDate } from "@/utils/datetime";
import { required } from "@/utils/formRules";
import {
  createNote,
  updateNote,
  deleteNote,
} from "@/services/clinicalNoteService";

export default {
  name: "EditClinicalNotesDialog",

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Histórico de Vida" },
    notes: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      editingId: null,
      form: { kind: "life_history", content: "" },
      formLoading: false,
      kindOptions: [
        { text: "Histórico de vida", value: "life_history" },
        { text: "Geral", value: "general" },
        { text: "Outro", value: "other" },
      ],
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddForm());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    // Regra dos campos (`:rules`) — ver utils/formRules.
    required,
    formatDate: (v) => formatDate(v, { fallback: "—" }),
    kindLabel(kind) {
      return (
        { life_history: "Histórico", general: "Geral", other: "Outro" }[kind] ||
        "Nota"
      );
    },

    kindColor(kind) {
      return (
        { life_history: "#6D28D9", general: "#0891B2", other: "#64748B" }[kind] ||
        "#64748B"
      );
    },

    openAddForm() {
      this.editingId = null;
      this.form = { kind: "life_history", content: "" };
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    openEditForm(item) {
      this.editingId = item.id;
      this.form = { kind: item.kind || "general", content: item.content || "" };
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    closeForm() {
      this.formDialogVisible = false;
      this.editingId = null;
      this.form = { kind: "life_history", content: "" };
    },

    async saveData() {
      // Campos obrigatórios vazios ficam em vermelho; não grava.
      if (!this.$refs.entryForm.validate()) return;
      this.formLoading = true;
      const payload = { kind: this.form.kind, content: this.form.content.trim() };
      try {
        if (this.editingId) {
          await updateNote(this.editingId, payload);
        } else {
          await createNote(this.patientId, payload);
        }
        this.success("Nota salva com sucesso!");
        this.closeForm();
        this.$emit("data-saved", payload);
      } catch (err) {
        console.error("Erro ao salvar nota:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar a nota");
      } finally {
        this.formLoading = false;
      }
    },

    async confirmDelete(item) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja remover esta nota?",
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;
      try {
        await deleteNote(item.id);
        this.success("Nota removida com sucesso!");
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir nota:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover a nota");
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid #ddd6fe !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}

.history-row td {
  border-bottom: 1px solid #ede9fe !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}
</style>
